import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { useAuthStore } from '@/core/auth/auth';
import { useSocket } from '@/shared/composables/useSocket';
import { formatCurrency } from '@/shared/utils/formatters';
import { getUiUpdateNotice, markUiUpdateNoticeRead } from '@/core/app/uiUpdateNotice';

export interface AppNotification {
  id: string;
  title: string;
  description: string;
  createTime: string;
  isRead: boolean;
  type: 'payment' | 'invoice' | 'credit' | 'system' | 'ui-update';
  branchId?: string | null;
  branchName?: string | null;
  qbCustomerId?: string | null;
  customerName?: string | null;
  amount?: number | null;
  docNumber?: string | null;
  referenceNo?: string | null;
  searchTerm?: string | null;
  remoteVersion?: string;
}

const STORAGE_KEY = 'fcrm_realtime_notifications';
const MAX_STORED_NOTIFICATIONS = 100;
const MAX_AGE_MS = 48 * 60 * 60 * 1000; // 48 hours auto-cleanup

export const useNotificationStore = defineStore('notification', () => {
  const storedList: AppNotification[] = (() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return [];
      const parsed: AppNotification[] = JSON.parse(raw);
      const now = Date.now();
      // Auto-prune entries older than 48 hours to manage high volume
      return parsed.filter((item) => {
        const itemTime = new Date(item.createTime).getTime();
        return !isNaN(itemTime) && now - itemTime < MAX_AGE_MS;
      }).slice(0, MAX_STORED_NOTIFICATIONS);
    } catch {
      return [];
    }
  })();

  const notifications = ref<AppNotification[]>(storedList);
  const isListenerActive = ref(false);

  function saveToStorage() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(notifications.value.slice(0, MAX_STORED_NOTIFICATIONS)));
    } catch (e) {
      console.warn('[NotificationStore] Failed to save notifications to storage:', e);
    }
  }

  /**
   * Evaluates if the current logged-in user is permitted to view notifications
   * for a given branch based on their role and assigned branch.
   */
  function checkBranchAccess(notifBranchId?: string | null, notifBranchName?: string | null): boolean {
    const authStore = useAuthStore();
    const user = authStore.user;
    if (!user) return false;

    const role = String(user.role || '').toLowerCase();
    // 1. Roles & permissions with all-branch access
    if (
      role === 'admin' ||
      role === 'superadmin' ||
      role === 'financeadmin' ||
      user.permissions?.allBranches === true ||
      user.permissions?.['creditCheck.allBranches'] === true
    ) {
      return true;
    }

    // 2. User assigned branch
    const rawBranch = user.branch_id || user.branch;
    if (!rawBranch) {
      return true;
    }

    const userBranchId = String(
      typeof rawBranch === 'object' ? (rawBranch._id || rawBranch.id || '') : rawBranch
    ).trim();

    const userBranchName = String(
      typeof rawBranch === 'object' ? (rawBranch.name || rawBranch.location || rawBranch.abbr || '') : ''
    ).trim().toLowerCase();

    if (notifBranchId && userBranchId) {
      return String(notifBranchId).trim().toLowerCase() === userBranchId.toLowerCase();
    }

    if (notifBranchName && userBranchName) {
      const nName = String(notifBranchName).trim().toLowerCase();
      return nName.includes(userBranchName) || userBranchName.includes(nName);
    }

    if (!notifBranchId && !notifBranchName) {
      return true;
    }

    return false;
  }

  /**
   * Adds a new notification if the current user has access to that branch.
   */
  function addNotification(notif: Omit<AppNotification, 'id' | 'isRead'>) {
    if (!checkBranchAccess(notif.branchId, notif.branchName)) {
      console.log('[NotificationStore] Suppressed notification outside user branch access:', notif);
      return;
    }

    const newEntry: AppNotification = {
      ...notif,
      id: `notif_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`,
      isRead: false,
    };

    notifications.value.unshift(newEntry);
    if (notifications.value.length > MAX_STORED_NOTIFICATIONS) {
      notifications.value = notifications.value.slice(0, MAX_STORED_NOTIFICATIONS);
    }
    saveToStorage();
  }

  /**
   * Processes a real-time socket payload (from billing:updated)
   */
  function handleRealtimeBillingEvent(payload: any) {
    if (!payload || !payload.action) return;

    if (!checkBranchAccess(payload.branchId, payload.branchName)) {
      return;
    }

    const isPayment = payload.action === 'payment';
    const isInvoice = payload.action === 'invoice';

    if (isPayment) {
      const custText = payload.customerName || `Customer #${payload.qbCustomerId || ''}`;
      const amountText = payload.totalAmount != null ? `؋${formatCurrency(payload.totalAmount)}` : '';
      const refText = payload.referenceNo ? `Ref: ${payload.referenceNo}` : (payload.paymentId ? `#${payload.paymentId}` : '');
      const docText = payload.docNumber ? `Invoice #${payload.docNumber}` : '';

      let description = custText;
      if (docText) {
        description += ` • Applied to ${docText}`;
      } else if (payload.unappliedAmount > 0) {
        description += ` • Available Credit: ؋${formatCurrency(payload.unappliedAmount)}`;
      }
      if (refText) {
        description += ` (${refText})`;
      }

      const isCreditApplied = payload.operation === 'CreditApplied';
      let title = `Payment Received ${amountText ? `(${amountText})` : ''}`.trim();
      if (isCreditApplied) {
        title = `Credit Applied ${amountText ? `(${amountText})` : ''}`.trim();
      }

      if (payload.isPaid && !description.includes('Marked Paid')) {
        description += ' (Marked Paid)';
      }

      const searchTerm = payload.docNumber || payload.customerName || payload.qbCustomerId || payload.referenceNo || '';

      addNotification({
        title,
        description,
        createTime: payload.timestamp || new Date().toISOString(),
        type: 'payment',
        branchId: payload.branchId || null,
        branchName: payload.branchName || null,
        qbCustomerId: payload.qbCustomerId || null,
        customerName: payload.customerName || null,
        amount: payload.totalAmount != null ? Number(payload.totalAmount) : null,
        docNumber: payload.docNumber || null,
        referenceNo: payload.referenceNo || null,
        searchTerm,
      });
    } else if (isInvoice) {
      const custText = payload.customerName || `Customer #${payload.qbCustomerId || ''}`;
      const docText = payload.docNumber || (payload.invoiceId ? `#${payload.invoiceId}` : 'Invoice');
      const isPaid = payload.isPaid || (payload.balance != null && Number(payload.balance) <= 0.01);
      const totalText = payload.totalAmount != null ? `؋${formatCurrency(payload.totalAmount)}` : '';

      const isCreate = String(payload.operation || '').toLowerCase() === 'create';
      const verb = isCreate ? 'Created' : 'Updated';

      let title = '';
      let description = '';

      if (isPaid) {
        title = `Invoice #${docText} Fully Paid`;
        description = `${custText} • ${totalText ? `${totalText} settled` : 'Paid in full'}`;
      } else {
        title = `Invoice #${docText} ${verb} ${totalText ? `(${totalText})` : ''}`.trim();
        const dueText = payload.balance != null ? `Due: ؋${formatCurrency(payload.balance)}` : '';
        description = `${custText}${dueText ? ` • ${dueText}` : ''}`;
      }

      const searchTerm = payload.docNumber || payload.customerName || payload.qbCustomerId || '';

      addNotification({
        title,
        description,
        createTime: payload.timestamp || new Date().toISOString(),
        type: 'invoice',
        branchId: payload.branchId || null,
        branchName: payload.branchName || null,
        qbCustomerId: payload.qbCustomerId || null,
        customerName: payload.customerName || null,
        amount: payload.totalAmount != null ? Number(payload.totalAmount) : null,
        docNumber: payload.docNumber || null,
        searchTerm,
      });
    }
  }

  /**
   * Fetches recent processed QuickBooks notifications from backend API
   * Ensures no notification is missed across page reloads, tab closure, or network reconnects.
   */
  async function fetchRecentNotifications() {
    try {
      const authStore = useAuthStore();
      if (!authStore.token) return;

      const { default: axiosInstance } = await import('@/shared/utils/axios');
      const res = await axiosInstance.get('/api/billing/notifications/recent');
      if (res.data?.success && Array.isArray(res.data.notifications)) {
        const backendNotifs: AppNotification[] = res.data.notifications;
        const backendIdSet = new Set(backendNotifs.map((n) => n.id));

        // Reconcile: If a webhook notification was deleted/cleared on server, remove it locally
        notifications.value = notifications.value.filter(
          (n) => !n.id.startsWith('notif_webhook_') || backendIdSet.has(n.id)
        );

        const currentIds = new Set(notifications.value.map((n) => n.id));

        for (const notif of backendNotifs) {
          const existing = notifications.value.find((n) => n.id === notif.id);
          if (existing) {
            if (existing.title !== notif.title || existing.description !== notif.description) {
              existing.title = notif.title;
              existing.description = notif.description;
            }
          } else if (!currentIds.has(notif.id) && checkBranchAccess(notif.branchId, notif.branchName)) {
            notifications.value.push(notif);
            currentIds.add(notif.id);
          }
        }

        notifications.value.sort((a, b) => new Date(b.createTime).getTime() - new Date(a.createTime).getTime());
        if (notifications.value.length > MAX_STORED_NOTIFICATIONS) {
          notifications.value = notifications.value.slice(0, MAX_STORED_NOTIFICATIONS);
        }
        saveToStorage();
      }
    } catch (err: any) {
      console.warn('[NotificationStore] Could not fetch recent notifications:', err?.message);
    }
  }

  /**
   * Initialize Socket.IO listeners once globally
   */
  function initSocketListeners() {
    fetchRecentNotifications();

    if (isListenerActive.value) return;
    isListenerActive.value = true;

    try {
      const { joinRoom, on: onSocket } = useSocket();
      joinRoom('billing');
      onSocket('billing:updated', handleRealtimeBillingEvent);
      onSocket('billing:notification-deleted', (data: { id?: string }) => {
        if (data?.id) {
          notifications.value = notifications.value.filter((n) => n.id !== data.id);
          saveToStorage();
        }
      });
      onSocket('billing:notifications-cleared', () => {
        notifications.value = notifications.value.filter((n) => !n.id.startsWith('notif_webhook_'));
        saveToStorage();
      });
      console.log('[NotificationStore] Global real-time billing notification listener registered');
    } catch (err: any) {
      console.warn('[NotificationStore] Failed to register socket listener:', err?.message);
    }
  }

  function markAsRead(id: string) {
    const item = notifications.value.find((n) => n.id === id);
    if (item) {
      item.isRead = true;
      saveToStorage();
    }
  }

  async function removeNotification(id: string) {
    notifications.value = notifications.value.filter((n) => n.id !== id);
    saveToStorage();

    if (id.startsWith('notif_webhook_')) {
      try {
        const { default: axiosInstance } = await import('@/shared/utils/axios');
        await axiosInstance.delete(`/api/billing/notifications/${encodeURIComponent(id)}`);
      } catch (err: any) {
        console.warn('[NotificationStore] Failed to sync notification deletion to server:', err?.message);
      }
    }
  }

  function markAllAsRead() {
    notifications.value.forEach((n) => {
      n.isRead = true;
    });
    markUiUpdateNoticeRead();
    saveToStorage();
  }

  async function clearAll() {
    notifications.value = [];
    saveToStorage();

    try {
      const { default: axiosInstance } = await import('@/shared/utils/axios');
      await axiosInstance.post('/api/billing/notifications/clear-all');
    } catch (err: any) {
      console.warn('[NotificationStore] Failed to sync clear-all to server:', err?.message);
    }
  }

  const allNotifications = computed<AppNotification[]>(() => {
    const list = [...notifications.value];
    const uiNotice = getUiUpdateNotice().value;
    if (uiNotice) {
      list.unshift({
        id: uiNotice.id,
        title: uiNotice.title,
        description: uiNotice.description,
        createTime: uiNotice.createTime,
        isRead: uiNotice.isRead,
        type: 'ui-update',
        remoteVersion: uiNotice.remoteVersion,
      });
    }
    return list;
  });

  const unreadCount = computed(() => {
    return allNotifications.value.filter((n) => !n.isRead).length;
  });

  return {
    notifications: allNotifications,
    unreadCount,
    addNotification,
    handleRealtimeBillingEvent,
    initSocketListeners,
    markAsRead,
    removeNotification,
    markAllAsRead,
    clearAll,
  };
});
